import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Pencil, Trash2, Undo2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { localeTagFor, type Lang } from "@/i18n/locale";

// ── ModuleHistoryList — the VYV generic record-history pattern ──────────────
// One reusable chronological list with period filter + per-item edit/delete,
// intended to replace the bespoke lists each module rolled by hand (Finance
// TxRow, Tasks TaskCard, FitnessHistory rows...). Adopting it means passing
// data + render config:
//
//   <ModuleHistoryList
//     items={rows}
//     getId={(r) => r.id}
//     getDate={(r) => r.created_at}
//     renderItem={(r) => <YourSummaryLine row={r} />}
//     onEdit={(r) => openEditModal(r)}          // module owns the edit UI
//     onDelete={async (r) => { await del(r.id); }} // list owns undo window
//     labels={{ ...translated strings... }}     // module owns i18n keys
//   />
//
// Design decisions:
// - Generic over T: zero knowledge of module schemas; date + id + render are
//   injected. i18n strings are injected too (labels), so the component never
//   hardcodes copy and never guesses a module's key namespace.
// - Period filter lives INSIDE (30/90/all days) — the repeated need every
//   module reimplements; controlled internally, computed via getDate.
// - Delete is OPTIMISTIC with a 5s "Undo" snackbar (replaces the old
//   AlertDialog confirm): the row leaves the list immediately and STAYS
//   hidden (committingIds) until the physical onDelete resolves, so it never
//   flickers back while the DB call is in flight. The delete commits when the
//   window expires, on the next delete, or on unmount (in-app navigation).
//   Caveat: a hard tab close / PWA kill during the 5s window abandons the
//   delete — fail-safe direction, the row simply stays. Undo cancels before
//   anything touches the DB, so "undo" is a no-op server-side — nothing to
//   re-create, nothing to race.
// - Edit is delegated: the list only surfaces the affordance. Modules already
//   have create forms; reusing them for edit (Finance pattern) beats a
//   generic form builder.

export interface ModuleHistoryLabels {
  edit?: string;
  delete?: string;
  empty: string;
  /** Snackbar text shown while the 5s undo window is open. */
  deletedToast: string;
  /** Undo button label. */
  undo: string;
  period30: string;
  period90: string;
  periodAll: string;
}

export interface ModuleHistoryListProps<T> {
  items: T[];
  getId: (item: T) => string;
  getDate: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => Promise<void> | void;
  // Per-item gating (default: allowed when the handler exists). Lets mixed
  // lists keep read-only rows — e.g. Fitness history where Strava imports
  // are immutable but manual gym sessions are editable/deletable.
  canEdit?: (item: T) => boolean;
  canDelete?: (item: T) => boolean;
  labels: ModuleHistoryLabels;
}

type PeriodDays = 30 | 90 | null;

const UNDO_WINDOW_MS = 5000;

const localeFor = (lang: Lang): string =>
  localeTagFor(lang);

export function ModuleHistoryList<T>({
  items,
  getId,
  getDate,
  renderItem,
  onEdit,
  onDelete,
  canEdit,
  canDelete,
  labels,
}: ModuleHistoryListProps<T>) {
  const { lang } = useLanguage();
  const [period, setPeriod] = useState<PeriodDays>(90);
  // Item hidden from the list while its undo window is open.
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);
  // Items whose onDelete is IN FLIGHT — kept hidden until it settles, so a
  // committed row never flickers back while the consumer's async delete +
  // refetch is still running (review 03/07).
  const [committingIds, setCommittingIds] = useState<ReadonlySet<string>>(new Set());

  // Refs so the unmount flush sees the live pending item / handlers (not a
  // stale closure). Single-pending policy: a second delete flushes the first.
  const pendingRef = useRef<{ item: T; timer: number } | null>(null);
  const onDeleteRef = useRef(onDelete);
  onDeleteRef.current = onDelete;
  const getIdRef = useRef(getId);
  getIdRef.current = getId;

  const commitDelete = useCallback((item: T) => {
    const id = getIdRef.current(item);
    setCommittingIds(prev => { const n = new Set(prev); n.add(id); return n; });
    Promise.resolve(onDeleteRef.current?.(item))
      // Consumers surface errors; on failure the row is still in `items`, so
      // un-hiding it below self-heals the list (the row renders again).
      .catch(() => {})
      .finally(() => {
        setCommittingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      });
  }, []);

  const flushPending = useCallback(() => {
    const p = pendingRef.current;
    if (!p) return;
    window.clearTimeout(p.timer);
    pendingRef.current = null;
    setPendingDelete(null);
    commitDelete(p.item);
  }, [commitDelete]);

  // Leaving the page (or the component unmounting) commits the pending
  // delete — the undo window shortens, but in-app navigation never loses it.
  useEffect(() => () => flushPending(), [flushPending]);

  const requestDelete = useCallback((item: T) => {
    flushPending();
    const timer = window.setTimeout(() => {
      if (pendingRef.current?.item !== item) return;
      pendingRef.current = null;
      setPendingDelete(null);
      commitDelete(item);
    }, UNDO_WINDOW_MS);
    pendingRef.current = { item, timer };
    setPendingDelete(item);
  }, [flushPending, commitDelete]);

  const undoDelete = useCallback(() => {
    const p = pendingRef.current;
    if (!p) return;
    window.clearTimeout(p.timer);
    pendingRef.current = null;
    setPendingDelete(null);
  }, []);

  const filtered = useMemo(() => {
    const sorted = [...items].sort(
      (a, b) => new Date(getDate(b)).getTime() - new Date(getDate(a)).getTime(),
    );
    const pendingId = pendingDelete === null ? null : getId(pendingDelete);
    const visible = sorted.filter(
      (i) => getId(i) !== pendingId && !committingIds.has(getId(i)),
    );
    if (period === null) return visible;
    const cutoff = Date.now() - period * 86_400_000;
    return visible.filter((i) => new Date(getDate(i)).getTime() >= cutoff);
  }, [items, period, getDate, getId, pendingDelete, committingIds]);

  const periods: Array<{ days: PeriodDays; label: string }> = [
    { days: 30, label: labels.period30 },
    { days: 90, label: labels.period90 },
    { days: null, label: labels.periodAll },
  ];

  const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString(localeFor(lang), {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  return (
    <div>
      {/* Period pills — Finance granularity-row style */}
      <div className="flex gap-2 mb-3">
        {periods.map((p) => (
          <button
            key={String(p.days)}
            onClick={() => setPeriod(p.days)}
            aria-pressed={period === p.days}
            className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
            style={
              period === p.days
                ? { background: "hsl(187 72% 50% / 0.18)", color: "hsl(187 72% 60%)", border: "1px solid hsl(187 72% 50% / 0.4)" }
                : { background: "hsl(222 18% 12%)", color: "hsl(222 10% 60%)", border: "1px solid hsl(222 18% 20%)" }
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{labels.empty}</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <div
              key={getId(item)}
              className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: "hsl(222 18% 10%)", border: "1px solid hsl(222 18% 18%)" }}
            >
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-muted-foreground mb-0.5">{formatDate(getDate(item))}</p>
                {renderItem(item)}
              </div>
              {onEdit && (canEdit ? canEdit(item) : true) && (
                <button
                  onClick={() => onEdit(item)}
                  aria-label={labels.edit ?? "edit"}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <Pencil size={15} style={{ color: "hsl(187 72% 60%)" }} />
                </button>
              )}
              {onDelete && (canDelete ? canDelete(item) : true) && (
                <button
                  onClick={() => requestDelete(item)}
                  aria-label={labels.delete ?? "delete"}
                  className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <Trash2 size={15} style={{ color: "hsl(0 72% 60%)" }} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Undo snackbar — floats above the BottomNav while the window is open */}
      {pendingDelete !== null && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 flex items-center gap-3 pl-4 pr-2 py-2 rounded-full shadow-lg"
          style={{
            background: "hsl(222 18% 14%)",
            border: "1px solid hsl(222 18% 24%)",
            boxShadow: "0 8px 32px hsl(0 0% 0% / 0.5)",
          }}
          role="status"
        >
          <span className="text-xs" style={{ color: "hsl(222 10% 78%)" }}>{labels.deletedToast}</span>
          <button
            onClick={undoDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold active:scale-95 transition-transform"
            style={{ background: "hsl(187 72% 50% / 0.18)", color: "hsl(187 72% 60%)", border: "1px solid hsl(187 72% 50% / 0.4)" }}
          >
            <Undo2 size={12} />
            {labels.undo}
          </button>
        </div>
      )}
    </div>
  );
}
